import EventCard from '@/app/(public)/components/cards/event-card'
import Error from '@/components/common/error'
import ArrowLeft from '@/components/icons/arrow-left'
import ArrowRight from '@/components/icons/arrow-right'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel'
import { Skeleton } from '@/components/ui/skeleton'
import useMediaQuery from '@/helpers/use-media-query'
import apiFetch from '@/store/helpers/api-fetch'
import useEmblaCarousel from 'embla-carousel-react'
import { useEffect, useState } from 'react'
import useSWRImmutable from 'swr/immutable'

export default function EventCarousel({ params, currentEventId, where = {} }) {
  const [mounted, setMounted] = useState(false)

  const { mediaQueryResult } = useMediaQuery('(min-width: 1024px)')

  useEffect(() => {
    setMounted(true)
  }, [])

  const { data: events, isLoading: eventsLoading } = useSWRImmutable(
    {
      url: `community/${params.communitySlug}/championship/${params.championshipSlug}/event`,
      searchParams: {
        where: {
          ...where,
          isPublished: true,
          active: true,
          id: {
            not: currentEventId,
          },
        },
        select: {
          id: true,
          active: true,
          deleteTs: true,
          isPublished: true,
          name: true,
          customTrackImgUrl: true,
          championship: {
            select: {
              id: true,
              name: true,
              slug: true,
              isSingleEvent: true,
              totalEventCount: true,
              gridPositions: true,
              isGridUnlimited: true,
              headerImgUrl: true,
              community: {
                select: {
                  id: true,
                  name: true,
                  slug: true,
                  logoImgUrl: true,
                  headerImgUrl: true,
                },
              },
              carClasses: {
                include: {
                  carClass: true,
                },
              },
            },
          },
          eventRoundNumber: true,
          startDateTime: true,
          finishDateTime: true,
          targetTimezone: true,
          sessions: {
            select: {
              targetDateTime: true,
              finishDateTime: true,
              targetTimezone: true,
            },
            orderBy: {
              targetDateTime: 'asc',
            },
          },
        },
        orderBy: {
          startDateTime: 'asc',
        },
      },
    },
    apiFetch,
  )

  return (
    <div className="mt-5 overflow-hidden desktop-xs:mt-10">
      {/* Carousel's */}
      {mounted && !eventsLoading ? (
        <div>
          {mediaQueryResult ? (
            <DesktopCarousel events={events} />
          ) : (
            <MobileCarousel events={events} />
          )}
        </div>
      ) : (
        <div className="screen-grid *:grid-indent">
          <Skeleton className="h-[700px] w-full rounded-sm tablet:h-[840px] desktop-xs:h-[770px] desktop-sm:h-[800px] desktop-lg:h-[817px]" />
        </div>
      )}
    </div>
  )
}

function MobileCarousel({ events }) {
  const [carouselRef, _carouselControls] = useEmblaCarousel({}, [])

  return (
    <div className="w-full font-inter">
      {!events ? (
        <Error
          icon="exclamation"
          size="hero"
          className="mx-auto my-16 w-full max-w-7xl bg-neutral-blue-800 text-white"
          error={{
            code: 404,
            message: `Unable to load other events.`,
          }}
        />
      ) : !events?.length > 0 ? (
        <div className="flex h-[131px] w-full items-center rounded-sm border border-neutral-blue-700 bg-neutral-blue-800 align-middle desktop-xs:h-[201px]">
          <h5 className="m-auto px-6 text-center">
            No other events in championship.
            {/* {selectedSplits
              ? 'in championship for selected split.'
              : 'in championship.'} */}
          </h5>
        </div>
      ) : (
        <div className="racing-outer" ref={carouselRef}>
          <div className="carousel-container">
            {events?.map((event, index) => (
              <div
                key={index}
                className={`carousel-slide ${index === 0 ? 'flex-[0_0_calc(100%-24px)]! pl-[36px]!' : ''} ${index + 1 === events.length ? 'flex-[0_0_calc(100%-24px)]! pr-[24px]!' : ''}`}
              >
                <EventCard eventFromParent={event} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function DesktopCarousel({ events }) {
  const [carouselController, setCarouselController] = useState(null)
  const [current, setCurrent] = useState(0)
  const [count, setCount] = useState(0)

  useEffect(() => {
    if (!carouselController) {
      return
    }

    setCount(carouselController.scrollSnapList().length)

    carouselController.on('select', () => {
      setCurrent(carouselController.selectedScrollSnap() + 1)
    })
  }, [carouselController])

  return (
    <div className="screen-grid relative">
      {!events ? (
        <Error
          icon="exclamation"
          size="hero"
          className="grid-indent mx-auto my-16 w-full max-w-7xl bg-red-600 text-white"
          error={{
            code: 404,
            message: `Unable to load other events.`,
          }}
        />
      ) : !events?.length > 0 ? (
        <div className="grid-indent flex h-[131px] w-full items-center rounded-sm border border-neutral-blue-700 bg-neutral-blue-800 align-middle desktop-xs:h-[201px]">
          <h5 className="m-auto text-center">
            No other events in championship
            {/* {selectedSplit?.id
              ? 'in championship for selected split.'
              : 'in championship.'} */}
          </h5>
        </div>
      ) : (
        <>
          <div className="absolute left-2 flex h-full w-fit flex-col justify-center align-middle desktop-sm:relative desktop-sm:left-0 desktop-sm:h-auto desktop-sm:w-full">
            {current > 0 && (
              <div
                className="carousel-btn-bg group flex h-full cursor-pointer flex-col justify-center rounded-sm bg-neutral-blue-900 align-middle transition-all"
                onClick={() => {
                  carouselController?.scrollPrev()
                  setCurrent((prev) => prev - 1)
                }}
              >
                <div className="mx-auto flex size-8 cursor-pointer items-center justify-center rounded-full border border-neutral-blue-600 bg-neutral-blue-700 align-middle group-hover:bg-neutral-blue-600 desktop-sm:size-12">
                  <ArrowLeft className="m-auto h-4 w-4 stroke-white stroke-2" />
                </div>
              </div>
            )}
          </div>
          <div className="col-span-10 flex flex-1 overflow-hidden">
            <Carousel
              setApi={setCarouselController}
              opts={{ slidesToScroll: 1 }}
              className="w-full"
            >
              <CarouselContent>
                {events?.map((event, index) => (
                  <CarouselItem key={index} className="min-h-full basis-1/3">
                    <EventCard eventFromParent={event} />
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </div>

          {events.length > 3 && (
            <div className="absolute right-2 flex h-full w-fit flex-col justify-center align-middle desktop-sm:relative desktop-sm:right-0 desktop-sm:h-auto desktop-sm:w-full">
              {current <= count && (
                <div
                  className="carousel-btn-bg group flex h-full cursor-pointer flex-col justify-center rounded-sm bg-neutral-blue-900 align-middle transition-all"
                  onClick={() => {
                    carouselController?.scrollNext()
                    setCurrent((prev) => prev + 1)
                  }}
                >
                  <div className="mx-auto flex size-8 cursor-pointer items-center justify-center rounded-full border border-neutral-blue-600 bg-neutral-blue-700 align-middle group-hover:bg-neutral-blue-600 desktop-sm:size-12">
                    <ArrowRight className="m-auto h-4 w-4 stroke-white stroke-2" />
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
